import { useMemo } from "react";
import UpsellSection from "./UpsellSection";

function ScorePills({ scores, primaryArchetype, secondaryArchetype }) {
  const sorted = useMemo(
    () => Object.entries(scores).sort(([, a], [, b]) => b - a),
    [scores]
  );

  return (
    <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2 mt-8 sm:mt-10 animate-fade-in-up-delay-4">
      {sorted.map(([name, score]) => (
        <span
          key={`pill-${name}`}
          data-testid={`score-pill-${name}`}
          className={`font-body text-[10px] sm:text-xs px-2 sm:px-3 py-1 sm:py-1.5 border ${
            name === primaryArchetype
              ? "border-[#F3B21A] text-[#F3B21A]"
              : name === secondaryArchetype
              ? "border-[#E66E6D] text-[#E66E6D]"
              : "border-[#0C6474] text-[#0C6474]"
          }`}
        >
          {name}: {score}
        </span>
      ))}
    </div>
  );
}

function ResultSection({ label, children, testId }) {
  return (
    <div className="mb-10 sm:mb-14" data-testid={testId}>
      <p className="font-body text-xs tracking-[0.25em] uppercase text-[#F3B21A] mb-4">
        {label}
      </p>
      {children}
    </div>
  );
}

function GoldDivider() {
  return <div className="w-10 h-[1px] bg-[#F3B21A] mx-auto mb-10 sm:mb-14" />;
}

function ReflectionList({ reflection }) {
  const questions = useMemo(() => {
    if (!reflection) return [];
    return Object.entries(reflection).map(([key, text]) => ({ id: key, text }));
  }, [reflection]);

  return (
    <div className="flex flex-col">
      {questions.map((q) => (
        <div
          key={`refl-${q.id}`}
          className="flex items-start gap-3 sm:gap-4 py-3 sm:py-4 border-b border-[#E8C8BE] last:border-b-0"
          data-testid={`reflection-${q.id}`}
        >
          <span className="w-1 h-1 rounded-full bg-[#F3B21A] mt-2 flex-shrink-0" />
          <p className="font-body text-xs sm:text-sm text-[#054452] font-light leading-relaxed">
            {q.text}
          </p>
        </div>
      ))}
    </div>
  );
}

export default function Results({ data }) {
  if (!data || !data.ai_results) return null;

  const { ai_results, primary_archetype, secondary_archetype, scores } = data;
  const { primary, secondary, reflection, truth_statement } = ai_results;

  return (
    <div data-testid="results-screen" className="min-h-screen">
      {/* Results Hero */}
      <section className="bg-[#054452] py-12 sm:py-16 lg:py-24 px-4 sm:px-6" data-testid="results-hero">
        <div className="max-w-[680px] mx-auto text-center">
          <p className="font-body text-[10px] sm:text-xs tracking-[0.25em] uppercase text-[#F3B21A] mb-4 sm:mb-6 animate-fade-in-up">
            Your Identity Pattern
          </p>
          <p className="font-body text-xs sm:text-sm text-[#F8D4CE] mb-2 sm:mb-3 animate-fade-in-up-delay-1">
            You are primarily operating as
          </p>
          <h1
            className="font-display text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-light text-white mb-3 sm:mb-4 animate-fade-in-up-delay-2"
            data-testid="primary-archetype-name"
          >
            The <em className="text-[#E66E6D]">{primary_archetype}</em>
          </h1>
          <p className="font-body text-xs sm:text-sm text-[#F8D4CE] mb-1 sm:mb-2 animate-fade-in-up-delay-3">
            with a secondary pattern of
          </p>
          <h2
            className="font-display text-xl sm:text-2xl lg:text-3xl font-light text-[#E66E6D] italic animate-fade-in-up-delay-3"
            data-testid="secondary-archetype-name"
          >
            The {secondary_archetype}
          </h2>

          <ScorePills
            scores={scores}
            primaryArchetype={primary_archetype}
            secondaryArchetype={secondary_archetype}
          />
        </div>
      </section>

      {/* Results Body */}
      <section className="bg-[#FDF6F3] py-12 sm:py-16 lg:py-20 px-4 sm:px-6">
        <div className="max-w-[680px] mx-auto">
          <ResultSection label="Your Pattern" testId="results-pattern">
            <div className="mirror-quote" data-testid="pattern-hook">
              {primary?.hook}
            </div>
            <p className="font-body text-sm sm:text-base text-[#054452] font-light leading-relaxed">
              {primary?.behaviors}
            </p>
          </ResultSection>

          <GoldDivider />

          <ResultSection label="What You Tell Yourself" testId="results-narrative">
            <p className="font-body text-sm sm:text-base text-[#054452] font-light leading-relaxed italic">
              {primary?.internal_narrative}
            </p>
          </ResultSection>

          <GoldDivider />

          <ResultSection label="The Real Cost" testId="results-cost">
            <p className="font-body text-sm sm:text-base text-[#054452] font-light leading-relaxed">
              {primary?.hidden_cost}
            </p>
          </ResultSection>

          <GoldDivider />

          <ResultSection label="The Identity Distortion" testId="results-distortion">
            <div className="mirror-quote" data-testid="distortion-quote">
              {primary?.identity_distortion}
            </div>
            <p className="font-body text-sm sm:text-base text-[#054452] font-light leading-relaxed">
              {primary?.reframe}
            </p>
          </ResultSection>

          <GoldDivider />

          <ResultSection label={`Secondary Pattern: The ${secondary_archetype}`} testId="results-secondary">
            <p className="font-body text-sm sm:text-base text-[#054452] font-light leading-relaxed mb-4">
              {secondary?.supporting_behavior}
            </p>
            <p className="font-body text-sm sm:text-base text-[#054452] font-light leading-relaxed">
              {secondary?.combined_effect}
            </p>
          </ResultSection>

          <GoldDivider />

          <ResultSection label="Reflection" testId="results-reflection">
            <ReflectionList reflection={reflection} />
          </ResultSection>

          <GoldDivider />

          <ResultSection label="The Truth" testId="results-truth">
            <div className="mirror-quote" data-testid="truth-statement">
              {truth_statement}
            </div>
            <p className="font-body text-xs text-[#0C6474] mt-4 italic">
              — Nijah Almond
            </p>
          </ResultSection>
        </div>
      </section>

      <UpsellSection />
    </div>
  );
}
